import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeftIcon, ArrowUpRightIcon, FileDiffIcon, RefreshCwIcon } from "lucide-react";
import { ComplianceOrganizationMissing } from "../components/regulation/ComplianceUnavailable";
import { ListSkeleton } from "../components/regulation/LoadingSkeletons";
import { RegulationPageShell } from "../components/regulation/RegulationPageShell";
import { InspectionStatusBadge } from "../components/regulation/inspector/InspectionStatus";
import { InspectionRunWorkspace } from "../components/regulation/inspector/InspectionRunWorkspace";
import { formatDuration, TRIGGER_LABEL } from "../components/regulation/inspector/inspectorView";
import { useRegulationOrg } from "../components/regulation/useRegulationOrg";
import { Button } from "../components/ui/button";
import { useInspection } from "../state/inspections";

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
  });
function RegulationInspectionRoute() {
  const { org: orgSegment, threadId } = Route.useParams();
  const { org, isLoading } = useRegulationOrg();
  const read = useInspection(org?.id ?? null, threadId);
  const back = (
    <Link
      to="/$org/compliance/inspector"
      params={{ org: orgSegment }}
      className="inline-flex w-fit items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
    >
      <ArrowLeftIcon aria-hidden className="size-3.5" />
      All inspections
    </Link>
  );
  const detail = read.snapshot;
  if (!detail)
    return (
      <RegulationPageShell eyebrow={back} title="Inspection">
        {() => (
          <div className="mt-6">
            {!org && !isLoading ? (
              <ComplianceOrganizationMissing />
            ) : read.missing ? (
              <div className="rounded-xl border border-dashed border-border p-8">
                <h2 className="text-sm font-medium">Inspection not found</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  This run is not available in this organization.
                </p>
              </div>
            ) : read.error ? (
              <div role="alert" className="space-y-3">
                <p className="text-sm text-muted-foreground">{read.error}</p>
                <Button variant="outline" size="sm" onClick={read.refresh}>
                  Try again
                </Button>
              </div>
            ) : (
              <ListSkeleton rows={4} />
            )}
          </div>
        )}
      </RegulationPageShell>
    );
  const { run } = detail;
  return (
    <RegulationPageShell
      eyebrow={back}
      title={
        run.review ? "Finding review" : run.trigger ? TRIGGER_LABEL[run.trigger] : "Inspection"
      }
      titleAside={<InspectionStatusBadge status={run.status} />}
      description={`Requested ${when(run.startedAt)}${run.completedAt ? ` · ${formatDuration(run.startedAt, run.completedAt)}` : ""}`}
      actions={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Refresh inspection"
            onClick={read.refresh}
          >
            <RefreshCwIcon className="size-4" />
          </Button>
          {run.review ? (
            <Button
              size="sm"
              variant={run.status === "completed" ? "default" : "outline"}
              render={
                <Link
                  to="/$org/compliance/findings/$findingId"
                  params={{ org: orgSegment, findingId: run.review.findingId }}
                  search={run.status === "completed" ? { tab: "changes" } : {}}
                />
              }
            >
              {run.status === "completed" ? (
                <>
                  <FileDiffIcon className="size-3.5" />
                  Review changes
                </>
              ) : (
                <>
                  Open finding
                  <ArrowUpRightIcon className="size-3.5" />
                </>
              )}
            </Button>
          ) : null}
        </div>
      }
    >
      {() => (
        <div className="mt-6 pb-10">
          {read.error ? (
            <div
              role="status"
              className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground"
            >
              <span>{read.error}</span>
              <Button size="xs" variant="outline" onClick={read.refresh}>
                Retry
              </Button>
            </div>
          ) : null}
          <InspectionRunWorkspace
            key={threadId}
            detail={detail}
            orgSegment={orgSegment}
            organizationId={org?.id ?? null}
          />
        </div>
      )}
    </RegulationPageShell>
  );
}
export const Route = createFileRoute("/$org/compliance/inspector_/$threadId")({
  component: RegulationInspectionRoute,
});
